import React, { useState, useRef, useEffect } from 'react';

export function ReportElement({ 
  id, 
  x, 
  y, 
  w, 
  h, 
  zIndex, 
  isActive, 
  onSelect, 
  onPositionChange, 
  onDragEnd,
  onContextMenu, 
  isEditable = true, 
  snapFn, 
  children, 
  zoneBadge,
  pageIndex,
  // Optional constraints
  minW = 16,
  minH = 12,
  maxW,
  maxH,
  // Visual feedback
  showDimensions = true,
  // Overflow behavior
  allowOverflow = false,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const cleanupRef = useRef(null);

  useEffect(() => {
    return () => { if (cleanupRef.current) cleanupRef.current(); };
  }, []);

  const handleDragStart = (e) => {
    if (!isEditable || e.button !== 0) return;
    e.stopPropagation();
    setIsDragging(true);
    
    const startMX = e.clientX;
    const startMY = e.clientY;
    const startX = x;
    const startY = y;

    const handleMove = (ev) => {
      const dx = ev.clientX - startMX;
      const dy = ev.clientY - startMY;
      let newX = Math.max(0, startX + dx);
      let newY = Math.max(0, startY + dy);
      if (snapFn) { 
        newX = snapFn(newX); 
        newY = snapFn(newY); 
      }
      onPositionChange({ x: newX, y: newY, w, h });
    };
    
    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      setIsDragging(false);
      cleanupRef.current = null;
      if (onDragEnd) onDragEnd();
    };
    
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    cleanupRef.current = () => { 
      window.removeEventListener('mousemove', handleMove); 
      window.removeEventListener('mouseup', handleUp); 
    };
  };

  const handleResizeStart = (e, corner) => {
    if (!isEditable || e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    
    const startMX = e.clientX;
    const startMY = e.clientY;
    const startW = w;
    const startH = h;
    const startX = x;
    const startY = y;

    const handleMove = (ev) => {
      const dx = ev.clientX - startMX;
      const dy = ev.clientY - startMY;
      
      let newX = startX;
      let newY = startY;
      let newW = startW;
      let newH = startH;

      // Handle different resize corners
      switch (corner) {
        case 'se': // Southeast (bottom-right)
          newW = Math.max(minW, startW + dx);
          newH = Math.max(minH, startH + dy);
          break;
        case 'sw': // Southwest (bottom-left)
          newW = Math.max(minW, startW - dx);
          newH = Math.max(minH, startH + dy);
          newX = startX + (startW - newW);
          break;
        case 'ne': // Northeast (top-right)
          newW = Math.max(minW, startW + dx);
          newH = Math.max(minH, startH - dy);
          newY = startY + (startH - newH);
          break;
        case 'nw': // Northwest (top-left)
          newW = Math.max(minW, startW - dx);
          newH = Math.max(minH, startH - dy);
          newX = startX + (startW - newW);
          newY = startY + (startH - newH);
          break;
        case 'n': // North (top edge)
          newH = Math.max(minH, startH - dy);
          newY = startY + (startH - newH);
          break;
        case 's': // South (bottom edge)
          newH = Math.max(minH, startH + dy);
          break;
        case 'e': // East (right edge)
          newW = Math.max(minW, startW + dx);
          break;
        case 'w': // West (left edge)
          newW = Math.max(minW, startW - dx);
          newX = startX + (startW - newW);
          break;
        default:
          break;
      }

      // Apply max constraints
      if (maxW) newW = Math.min(maxW, newW);
      if (maxH) newH = Math.min(maxH, newH);
      // Clamp to non-negative position
      if (newX < 0) { newW += newX; newX = 0; }
      if (newY < 0) { newH += newY; newY = 0; }
      newW = Math.max(minW, newW);
      newH = Math.max(minH, newH);

      // Snap if enabled
      if (snapFn) { 
        newW = snapFn(newW); 
        newH = snapFn(newH);
        newX = snapFn(newX);
        newY = snapFn(newY);
      }

      onPositionChange({ x: newX, y: newY, w: newW, h: newH });
    };
    
    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      setIsResizing(false);
      if (onDragEnd) onDragEnd();
    };
    
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  // Handle styles for resize cursors
  const resizeHandleBase = "absolute w-2.5 h-2.5 bg-primary rounded-full border border-background z-20";
  const edgeHandleBase = "absolute bg-primary/0 hover:bg-primary/20 z-10";

  return (
    <div
      data-element-id={id}
      data-page-index={pageIndex}
      className={`absolute transition-shadow ${
        isActive ? 'ring-[5px] ring-primary ring-offset-1 ring-offset-background shadow-lg' : 'hover:ring-2 hover:ring-primary/25'
      } ${isEditable ? 'cursor-move' : ''} ${isDragging ? 'opacity-80' : ''} ${isResizing ? 'select-none' : ''}`}
      style={{ 
        left: x, 
        top: y, 
        width: w, 
        height: h, 
        zIndex: zIndex || 10,
        overflow: allowOverflow ? 'visible' : 'hidden',
      }}
      onMouseDown={isEditable ? handleDragStart : undefined}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      onContextMenu={(e) => { 
        e.preventDefault(); 
        e.stopPropagation(); 
        if (onContextMenu) onContextMenu(e); 
      }}
    >
      {/* Content */}
      <div className={`w-full h-full ${allowOverflow ? '' : 'overflow-hidden'}`}>
        {children}
      </div>

      {/* Zone badge */}
      {zoneBadge && isActive && (
        <span className="absolute -top-4 left-0 text-[7px] text-muted-foreground/70 bg-background px-1 rounded z-30">
          {zoneBadge}
        </span>
      )}

      {/* Coordinates/dimensions display when active */}
      {isActive && isEditable && showDimensions && (
        <span className="absolute -bottom-4 left-0 text-[7px] text-muted-foreground/70 font-mono bg-background px-1 rounded z-30">
          {Math.round(x)},{Math.round(y)} {Math.round(w)}×{Math.round(h)}
          {pageIndex !== undefined && <span className="ml-1 text-primary/50">P{pageIndex + 1}</span>}
        </span>
      )}

      {/* Selection handles - only show when active and editable */}
      {isActive && isEditable && (
        <>
          {/* Corner handles */}
          <div 
            className={`${resizeHandleBase} -top-1 -left-1 cursor-nw-resize`} 
            onMouseDown={(e) => handleResizeStart(e, 'nw')} 
          />
          <div 
            className={`${resizeHandleBase} -top-1 -right-1 cursor-ne-resize`} 
            onMouseDown={(e) => handleResizeStart(e, 'ne')} 
          />
          <div 
            className={`${resizeHandleBase} -bottom-1 -left-1 cursor-sw-resize`} 
            onMouseDown={(e) => handleResizeStart(e, 'sw')} 
          />
          <div 
            className={`${resizeHandleBase} -bottom-1 -right-1 cursor-se-resize`} 
            onMouseDown={(e) => handleResizeStart(e, 'se')} 
          />

          {/* Edge handles for more precise resizing */}
          <div 
            className={`${edgeHandleBase} top-0 left-2 right-2 h-1 cursor-n-resize -translate-y-1/2`}
            onMouseDown={(e) => handleResizeStart(e, 'n')} 
          />
          <div 
            className={`${edgeHandleBase} bottom-0 left-2 right-2 h-1 cursor-s-resize translate-y-1/2`}
            onMouseDown={(e) => handleResizeStart(e, 's')} 
          />
          <div 
            className={`${edgeHandleBase} left-0 top-2 bottom-2 w-1 cursor-w-resize -translate-x-1/2`}
            onMouseDown={(e) => handleResizeStart(e, 'w')} 
          />
          <div 
            className={`${edgeHandleBase} right-0 top-2 bottom-2 w-1 cursor-e-resize translate-x-1/2`}
            onMouseDown={(e) => handleResizeStart(e, 'e')} 
          />
        </>
      )}
    </div>
  );
}

// Helper component for page break marker
export function PageBreakMarker({ onRemove, isEditable = true }) {
  return (
    <div 
      className="relative w-full h-6 flex items-center justify-center my-2 group"
      data-element-type="page-break"
    >
      <div className="absolute inset-x-0 top-1/2 h-px bg-amber-400/50 -translate-y-1/2" />
      <div className="relative bg-amber-500/20 border border-amber-400/40 rounded-full px-3 py-0.5 flex items-center gap-1.5">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          className="w-3 h-3 text-amber-500"
        >
          <rect x="5" y="2" width="14" height="20" rx="2" />
          <line x1="2" y1="12" x2="5" y2="12" />
          <line x1="19" y1="12" x2="22" y2="12" />
        </svg>
        <span className="text-[8px] text-amber-600 font-medium uppercase tracking-wide">Page Break</span>
        {isEditable && onRemove && (
          <button 
            onClick={onRemove}
            className="opacity-0 group-hover:opacity-100 ml-1 p-0.5 rounded hover:bg-amber-500/30 text-amber-500 hover:text-amber-600 transition-opacity"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              className="w-2.5 h-2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
